import { FormEvent, type ElementType, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowRight,
  Award,
  BarChart3,
  Check,
  ChevronDown,
  CircleDollarSign,
  ClipboardCheck,
  Flame,
  Inbox,
  LineChart,
  Lock,
  Map as MapIcon,
  PackageCheck,
  Plus,
  Settings,
  ShoppingCart,
  Sparkles,
  TestTube2,
  Trash2,
  Trophy,
  Truck,
} from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { ProgressBar } from '@/components/ui/ProgressBar';
import { Skeleton } from '@/components/ui/Skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { EmptyState } from '@/components/EmptyState';
import { Modal } from '@/components/ui/Modal';
import {
  dashboardApi,
  type DashboardAchievement,
  type DashboardChecklistScope,
  type DashboardChecklistTask,
  type DashboardHeatmap,
  type DashboardLevel,
  type DashboardSummary,
} from '@/lib/api';
import { getSocket, onSocketReady } from '@/lib/socket';

const plnFormatter = new Intl.NumberFormat('pl-PL', {
  style: 'currency',
  currency: 'PLN',
  maximumFractionDigits: 0,
});

const numberFormatter = new Intl.NumberFormat('pl-PL');

const queryKeys = {
  summary: ['dashboard', 'summary'] as const,
  checklist: (scope: DashboardChecklistScope) => ['dashboard', 'checklist', scope] as const,
  heatmap: ['dashboard', 'heatmap'] as const,
  achievements: ['dashboard', 'achievements'] as const,
  levels: ['dashboard', 'levels'] as const,
};

const DASHBOARD_PAGE_CLASS = '-m-8 min-h-[calc(100vh-4rem)] space-y-4 p-4 sm:p-6 lg:p-8';

function formatPLN(value: number): string {
  return plnFormatter.format(value || 0);
}

function formatNumber(value: number): string {
  return numberFormatter.format(value || 0);
}

function formatCompactPLN(value: number): string {
  if (value >= 1_000_000) return `${formatNumber(value / 1_000_000)} mln zł`;
  if (value >= 1_000) return `${formatNumber(value / 1_000)} tys. zł`;
  return formatPLN(value);
}

function formatCategory(value?: string | null): string {
  if (!value) return 'Rada';
  return value.charAt(0).toLocaleUpperCase('pl-PL') + value.slice(1);
}

function cn(...classes: Array<string | false | null | undefined>): string {
  return classes.filter(Boolean).join(' ');
}

function Sparkline({ values, tone = 'accent' }: { values?: number[]; tone?: 'accent' | 'success' | 'warning' | 'danger' }) {
  const points = useMemo(() => {
    const data = values?.length ? values : Array(14).fill(0);
    const max = Math.max(...data, 1);
    return data.map((value, index) => {
      const x = (index / Math.max(data.length - 1, 1)) * 100;
      const y = 36 - (value / max) * 32;
      return `${x},${y}`;
    }).join(' ');
  }, [values]);

  const stroke = tone === 'success'
    ? 'rgb(var(--success))'
    : tone === 'warning'
      ? 'rgb(var(--warning))'
      : tone === 'danger'
        ? 'rgb(var(--danger))'
        : 'rgb(var(--accent-primary))';

  return (
    <svg viewBox="0 0 100 40" className="h-10 w-full" aria-hidden="true">
      <polyline points={points} fill="none" stroke={stroke} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" opacity="0.9" />
    </svg>
  );
}

function Delta({ value }: { value?: number }) {
  const delta = value ?? 0;
  const positive = delta >= 0;
  return (
    <span className={cn('text-xs font-semibold tabular-nums', positive ? 'text-success' : 'text-danger')}>
      {positive ? '+' : ''}{delta.toFixed(1)}%
    </span>
  );
}

function MetricTile({
  label,
  value,
  delta,
  sparkline,
  icon: Icon,
  to,
  tone = 'accent',
  className,
  period,
}: {
  label: string;
  value: string;
  delta?: number;
  sparkline?: number[];
  icon: ElementType;
  to: string;
  tone?: 'accent' | 'success' | 'warning' | 'danger';
  className?: string;
  period?: string;
}) {
  return (
    <Link to={to} className={cn('block focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary/40 rounded-card', className)} aria-label={`${label}: ${value}`}>
      <Card variant="interactive" padding="sm" className="h-full min-h-[156px] overflow-hidden">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xxs font-semibold uppercase tracking-normal text-text-muted">{label}</p>
            {period && <p className="mt-1 text-[10px] font-medium uppercase text-text-disabled">{period}</p>}
            <p className="mt-2 text-2xl lg:text-3xl font-bold tabular-nums text-text-primary truncate">{value}</p>
          </div>
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-surface-hover text-accent-primary">
            <Icon className="h-5 w-5" />
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between gap-3">
          <Delta value={delta} />
          <span className="text-xxs text-text-muted">vs poprzedni okres</span>
        </div>
        <div className="mt-2">
          <Sparkline values={sparkline} tone={tone} />
        </div>
      </Card>
    </Link>
  );
}

function LevelMapImage({
  src,
  alt,
  locked,
  fill = false,
}: {
  src?: string | null;
  alt: string;
  locked: boolean;
  fill?: boolean;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const element = containerRef.current;
    if (!element || shouldLoad) return undefined;

    if (!('IntersectionObserver' in window)) {
      setShouldLoad(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        setShouldLoad(true);
        observer.disconnect();
      },
      { rootMargin: '160px' },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [shouldLoad]);

  const wrapperClassName = cn('relative w-full overflow-hidden bg-surface-hover', fill ? 'absolute inset-0 h-full' : 'aspect-square');

  if (!src) {
    return (
      <div ref={containerRef} className={wrapperClassName}>
        <div className="absolute inset-0 scale-110 bg-gradient-to-br from-accent-primary/40 via-surface-muted to-accent-deep/50 blur-md" />
        <div className="absolute inset-0 bg-surface-base/35 backdrop-blur-[3px]" />
        <div className="absolute inset-x-6 inset-y-8 rounded-full border border-border-subtle bg-surface-hover/40 blur-sm" />
      </div>
    );
  }

  return (
    <div ref={containerRef} className={wrapperClassName}>
      {!loaded && (
        <div className="absolute inset-0 animate-pulse bg-gradient-to-br from-surface-hover via-surface-muted to-surface-hover" />
      )}
      {shouldLoad && (
        <img
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          onLoad={() => setLoaded(true)}
          className={cn(
            'h-full w-full object-cover transition-all duration-700',
            loaded ? (locked ? 'opacity-[0.58]' : 'opacity-100') : 'opacity-0',
            locked ? 'scale-105 blur-[2px] grayscale' : '',
          )}
        />
      )}
    </div>
  );
}

function LevelMapModal({
  isOpen,
  onClose,
  levels,
  isLoading,
  currentLevel,
}: {
  isOpen: boolean;
  onClose: () => void;
  levels?: DashboardLevel[];
  isLoading: boolean;
  currentLevel: number;
}) {
  const [selectedLevelNumber, setSelectedLevelNumber] = useState(currentLevel);
  const selectedDetailRef = useRef<HTMLDivElement | null>(null);
  const groups = useMemo(() => {
    const byRank = new Map<string, { rank: string; rankName: string; color: string; levels: DashboardLevel[] }>();
    for (const level of levels ?? []) {
      const key = `${level.rank}:${level.rankName}`;
      const existing = byRank.get(key);
      if (existing) {
        existing.levels.push(level);
      } else {
        byRank.set(key, {
          rank: level.rank,
          rankName: level.rankName,
          color: level.color,
          levels: [level],
        });
      }
    }

    return [...byRank.values()].map(group => ({
      ...group,
      levels: [...group.levels].sort((a, b) => a.level - b.level),
    }));
  }, [levels]);
  const selectedLevel = useMemo(() => {
    if (!levels?.length) return null;
    return levels.find(level => level.level === selectedLevelNumber)
      ?? levels.find(level => level.level === currentLevel)
      ?? levels[0];
  }, [currentLevel, levels, selectedLevelNumber]);
  const selectedUnlocked = !!selectedLevel?.unlocked;
  const selectedRankKey = selectedLevel ? `${selectedLevel.rank}-${selectedLevel.rankName}` : null;

  useEffect(() => {
    if (!isOpen) return;
    setSelectedLevelNumber(currentLevel);
  }, [currentLevel, isOpen]);

  useEffect(() => {
    if (!isOpen || isLoading || !selectedLevel) return undefined;
    const timeout = window.setTimeout(() => {
      selectedDetailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 80);
    return () => window.clearTimeout(timeout);
  }, [isLoading, isOpen, selectedLevel]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Mapa poziomów"
      description="Zdobyte poziomy są widoczne. Kolejne pozostają zablokowane do czasu awansu."
      size="xl"
    >
      <div className="space-y-6">
        {isLoading && Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="space-y-3">
            <Skeleton className="h-5 w-56" />
            <div className="grid grid-cols-5 gap-2">
              {Array.from({ length: 5 }).map((__, itemIndex) => <Skeleton key={itemIndex} className="aspect-square" />)}
            </div>
          </div>
        ))}

        {!isLoading && groups.map((group) => (
          <section key={`${group.rank}-${group.rankName}`} className="border-b border-border-subtle pb-5 last:border-b-0 last:pb-0">
            <h3 className="mb-3 text-sm font-semibold text-text-primary" style={{ color: group.color }}>
              Ranga {group.rank} - {group.rankName}
            </h3>
            {selectedLevel && selectedRankKey === `${group.rank}-${group.rankName}` && (
              <div ref={selectedDetailRef} className="mb-4 grid gap-4 rounded-xl border border-border-subtle bg-surface-hover/25 p-3 md:grid-cols-[minmax(180px,1fr)_minmax(0,2fr)]">
                <div className="relative min-h-[260px] overflow-hidden rounded-lg border border-border-subtle bg-surface-hover">
                  <LevelMapImage
                    src={selectedLevel.imagePath}
                    alt={selectedUnlocked && selectedLevel.name ? selectedLevel.name : `Zablokowany poziom ${selectedLevel.level}`}
                    locked={!selectedUnlocked}
                    fill
                  />
                  {!selectedUnlocked && (
                    <div className="absolute inset-0 flex items-center justify-center bg-surface-base/20">
                      <span className="flex h-11 w-11 items-center justify-center rounded-full border border-border bg-surface-raised/90 text-text-secondary shadow-lg">
                        <Lock className="h-5 w-5" />
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex min-w-0 flex-col justify-center">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="accent" leftIcon={<Trophy className="h-3.5 w-3.5" />}>Poziom {selectedLevel.level}</Badge>
                    <span className="inline-flex items-center gap-1 rounded-badge border border-accent-primary/25 bg-accent-primary/10 px-2.5 py-1 text-xs font-medium text-accent-primary">
                      <CircleDollarSign className="h-3.5 w-3.5" />
                      {formatPLN(selectedLevel.threshold)}
                    </span>
                    <span
                      className="inline-flex items-center rounded-badge border px-2.5 py-1 text-xs font-medium"
                      style={{ borderColor: `${selectedLevel.color}55`, backgroundColor: `${selectedLevel.color}14`, color: selectedLevel.color }}
                    >
                      Ranga {selectedLevel.rank} - {selectedLevel.rankName}
                    </span>
                  </div>
                  <h3 className="mt-4 text-xl font-bold text-text-primary">
                    {selectedUnlocked ? selectedLevel.name : 'Poziom zablokowany'}
                  </h3>
                  <p className="mt-2 font-serif text-base leading-7 text-text-secondary">
                    {selectedUnlocked && selectedLevel.description
                      ? selectedLevel.description
                      : `Osiągnij próg ${formatPLN(selectedLevel.threshold)}, żeby odblokować ten poziom i poznać jego opis.`}
                  </p>
                </div>
              </div>
            )}
            <div className="grid grid-cols-5 gap-2">
              {group.levels.map((item) => {
                const unlocked = item.unlocked;
                const isCurrent = item.level === currentLevel;

                return (
                  <button
                    type="button"
                    key={item.level}
                    onClick={() => setSelectedLevelNumber(item.level)}
                    className={cn(
                      'relative overflow-hidden rounded-lg border bg-surface-hover/40 text-left transition-colors hover:border-accent-primary/40 hover:bg-surface-hover/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary/40',
                      isCurrent ? 'border-accent-primary' : 'border-border-subtle',
                      selectedLevelNumber === item.level && 'border-accent-primary shadow-glass',
                    )}
                    title={unlocked && item.name ? `Poziom ${item.level} - ${item.name}` : `Zablokowany poziom ${item.level}`}
                  >
                    <LevelMapImage
                      src={item.imagePath}
                      alt={unlocked && item.name ? item.name : `Zablokowany poziom ${item.level}`}
                      locked={!unlocked}
                    />
                    <div className="absolute inset-x-0 bottom-0 z-20 bg-surface-base/80 px-1.5 py-1 text-center backdrop-blur">
                      <span className="inline-flex max-w-full items-center justify-center gap-1 text-[10px] font-bold tabular-nums text-text-primary">
                        <span>L{item.level}</span>
                        <span className="rounded-full border border-border bg-surface-hover/80 px-1 py-0.5 text-[9px] text-accent-primary">
                          {formatCompactPLN(item.threshold)}
                        </span>
                      </span>
                    </div>
                    {!unlocked && (
                      <div className="absolute inset-0 z-10 flex items-center justify-center bg-surface-base/25">
                        <span className="flex h-8 w-8 items-center justify-center rounded-full border border-border bg-surface-raised/90 text-text-secondary shadow-lg">
                          <Lock className="h-4 w-4" />
                        </span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </Modal>
  );
}

function CharacterHero({ summary }: { summary: DashboardSummary }) {
  const { gamification } = summary;
  const [isLevelMapOpen, setIsLevelMapOpen] = useState(false);
  const levelsQuery = useQuery({
    queryKey: queryKeys.levels,
    queryFn: async () => (await dashboardApi.getLevels()).data.data,
    enabled: isLevelMapOpen,
    staleTime: 30 * 60_000,
    refetchOnWindowFocus: false,
  });
  const level = gamification.level;
  const accent = gamification.color || '#9CA3AF';
  const imagePath = gamification.imagePath || '/dashboard-levels/R1L1.webp';
  const maxLevel = gamification.maxLevel || 100;
  const description = gamification.description || 'Skumulowany przychód buduje aktualny poziom operatora.';
  const remaining = gamification.nextThreshold === null
    ? 0
    : Math.max(0, gamification.nextThreshold - gamification.cumulativeRevenue);

  return (
    <>
      <Card padding="md" className="relative col-span-12 flex min-h-[340px] overflow-hidden lg:col-span-8" style={{ borderColor: `${accent}66` }}>
        <div className="pointer-events-none absolute inset-0 opacity-30" style={{ background: `radial-gradient(circle at 18% 24%, ${accent}33, transparent 34%)` }} />
        <button
          type="button"
          onClick={() => setIsLevelMapOpen(true)}
          className="absolute right-4 top-4 z-10 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border bg-surface-hover text-text-muted transition-colors hover:border-accent-primary/40 hover:text-accent-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary/40"
          aria-label="Otwórz mapę poziomów"
        >
          <MapIcon className="h-4 w-4" />
        </button>
        <div className="relative flex flex-1 flex-col gap-6 pr-0 sm:flex-row sm:items-center sm:pr-8">
          <button
            type="button"
            onClick={() => setIsLevelMapOpen(true)}
            className="relative aspect-square w-full max-w-[280px] shrink-0 rounded-[24px] p-2 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary/40 sm:h-[280px] sm:w-[280px]"
            aria-label={`Otwórz mapę poziomów z postacią poziomu ${level}`}
          >
            <span
              className="dojo-avatar-ring pointer-events-none absolute inset-0 rounded-[28px] opacity-80"
            />
            <div
              className="relative h-full w-full overflow-hidden rounded-[20px] border bg-surface-hover"
              style={{ borderColor: `${accent}88`, boxShadow: `0 0 36px ${accent}24` }}
            >
              <img
                src={imagePath}
                alt={gamification.levelName}
                className="dojo-avatar-motion aspect-square h-full w-full object-cover"
                decoding="async"
              />
              <span
                className="absolute bottom-3 left-3 rounded-full border bg-surface-base/80 px-2.5 py-1 text-[10px] font-bold tabular-nums backdrop-blur"
                style={{ borderColor: `${accent}66`, color: accent }}
              >
                L{level}
              </span>
            </div>
          </button>
          <div className="min-w-0 flex-1 self-center">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="accent" leftIcon={<Trophy className="h-3.5 w-3.5" />}>Poziom {level} / {maxLevel}</Badge>
              <span className="inline-flex items-center gap-1 rounded-badge border border-accent-primary/25 bg-accent-primary/10 px-2.5 py-1 text-xs font-medium text-accent-primary">
                <CircleDollarSign className="h-3.5 w-3.5" />
                {formatPLN(gamification.currentThreshold)}
              </span>
              {gamification.rank && gamification.rankName && (
                <span
                  className="inline-flex items-center rounded-badge border px-2.5 py-1 text-xs font-medium"
                  style={{ borderColor: `${accent}55`, backgroundColor: `${accent}14`, color: accent }}
                >
                  Ranga {gamification.rank} - {gamification.rankName}
                </span>
              )}
            </div>
            <h1 className="mt-4 text-2xl font-bold text-text-primary">{gamification.levelName}</h1>
            <p className="mt-2 font-serif text-base leading-7 text-text-secondary">{description}</p>
            <div className="mt-4">
              <span className="inline-flex items-center gap-2 rounded-badge border border-success-border bg-success-bg px-3 py-1.5 text-sm font-semibold text-success">
                <CircleDollarSign className="h-4 w-4" />
                Skumulowany przychód: {formatPLN(gamification.cumulativeRevenue)}
              </span>
            </div>
            <div className="mt-5">
              {gamification.nextThreshold === null ? (
                <div className="rounded-xl border border-warning-border bg-warning-bg px-4 py-3 text-sm text-warning">MAX - osiągnięto najwyższy poziom.</div>
              ) : (
                <ProgressBar
                  value={gamification.progressPct}
                  size="lg"
                  showPercent
                  aria-label="Postęp do następnego poziomu"
                  label={`Do następnego poziomu brakuje ${formatPLN(remaining)}`}
                />
              )}
            </div>
          </div>
        </div>
      </Card>
      <LevelMapModal
        isOpen={isLevelMapOpen}
        onClose={() => setIsLevelMapOpen(false)}
        levels={levelsQuery.data}
        isLoading={levelsQuery.isLoading}
        currentLevel={level}
      />
    </>
  );
}

function GoalTile({ summary }: { summary: DashboardSummary }) {
  const queryClient = useQueryClient();
  const now = new Date();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [monthlyDraft, setMonthlyDraft] = useState(summary.goal.revenueGoal ? String(Math.round(summary.goal.revenueGoal)) : '');
  const [annualDraft, setAnnualDraft] = useState(summary.goal.annualRevenueGoal ? String(Math.round(summary.goal.annualRevenueGoal)) : '');
  const monthName = new Intl.DateTimeFormat('pl-PL', { month: 'long' }).format(now);
  useEffect(() => {
    setMonthlyDraft(summary.goal.revenueGoal ? String(Math.round(summary.goal.revenueGoal)) : '');
    setAnnualDraft(summary.goal.annualRevenueGoal ? String(Math.round(summary.goal.annualRevenueGoal)) : '');
  }, [summary.goal.revenueGoal, summary.goal.annualRevenueGoal]);
  const goalMutation = useMutation({
    mutationFn: async () => {
      const monthlyValue = monthlyDraft.trim() === '' ? null : Number(monthlyDraft);
      const annualValue = annualDraft.trim() === '' ? null : Number(annualDraft);
      if (
        (monthlyValue !== null && (!Number.isFinite(monthlyValue) || monthlyValue < 0))
        || (annualValue !== null && (!Number.isFinite(annualValue) || annualValue < 0))
      ) {
        throw new Error('Invalid goal value');
      }

      const requests = [];
      if (monthlyValue !== null) {
        requests.push(dashboardApi.updateGoal({
          year: now.getFullYear(),
          month: now.getMonth() + 1,
          revenueGoal: monthlyValue,
        }));
      }
      if (annualValue !== null) {
        requests.push(dashboardApi.updateAnnualGoal({
          year: now.getFullYear(),
          revenueGoal: annualValue,
        }));
      }
      await Promise.all(requests);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.summary });
      queryClient.invalidateQueries({ queryKey: queryKeys.achievements });
      setIsSettingsOpen(false);
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    goalMutation.mutate();
  };

  return (
    <Card padding="md" className="col-span-12 lg:col-span-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xxs font-semibold uppercase text-text-muted">Cele sprzedaży</p>
          <h2 className="mt-2 flex flex-wrap items-center gap-2 text-2xl font-bold text-text-primary capitalize">
            {monthName}
            <span className="inline-flex items-center gap-1 rounded-full border border-warning-border bg-warning-bg px-2 py-0.5 text-xs font-semibold text-warning">
              <Flame className="h-3.5 w-3.5" />
              {summary.gamification.checklistStreak} dni
            </span>
          </h2>
        </div>
        <button
          type="button"
          onClick={() => setIsSettingsOpen(true)}
          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border bg-surface-hover text-text-muted transition-colors hover:border-accent-primary/40 hover:text-accent-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary/40"
          aria-label="Ustaw cele sprzedaży"
        >
          <Settings className="h-4 w-4" />
        </button>
      </div>

      <div className="mt-6 space-y-4">
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm font-semibold text-text-primary">Cel miesięczny</span>
            <span className="text-xs text-text-muted">{summary.goal.revenueGoal ? formatPLN(summary.goal.revenueGoal) : 'Nie ustawiono'}</span>
          </div>
          <ProgressBar
            value={summary.goal.progressPct}
            size="lg"
            showPercent
            label={summary.goal.revenueGoal
              ? `${formatPLN(summary.goal.revenueMTD)} z ${formatPLN(summary.goal.revenueGoal)}`
              : `${formatPLN(summary.goal.revenueMTD)} w tym miesiącu`}
          />
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm font-semibold text-text-primary">Cel roczny</span>
            <span className="text-xs text-text-muted">{summary.goal.annualRevenueGoal ? formatPLN(summary.goal.annualRevenueGoal) : 'Nie ustawiono'}</span>
          </div>
          <ProgressBar
            value={summary.goal.annualProgressPct}
            size="lg"
            showPercent
            label={summary.goal.annualRevenueGoal
              ? `${formatPLN(summary.goal.revenueYTD)} z ${formatPLN(summary.goal.annualRevenueGoal)}`
              : `${formatPLN(summary.goal.revenueYTD)} w tym roku`}
          />
        </div>
      </div>

      <Modal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        title="Cele sprzedaży"
        description="Ustaw miesięczny i roczny cel przychodu brutto dla dashboardu."
        size="md"
        footer={(
          <div className="flex items-center justify-between gap-3">
            <Button type="submit" form="dashboard-goal-form" loading={goalMutation.isPending} leftIcon={<Check />}>
              Zapisz
            </Button>
            <Button type="button" variant="secondary" onClick={() => setIsSettingsOpen(false)}>
              Anuluj
            </Button>
          </div>
        )}
      >
        <form id="dashboard-goal-form" onSubmit={submit} className="space-y-4">
          <label className="block">
            <span className="mb-1.5 block text-xs font-semibold text-text-secondary">Cel miesięczny</span>
            <Input
              type="number"
              min="0"
              step="100"
              value={monthlyDraft}
              onChange={(event) => setMonthlyDraft(event.target.value)}
              placeholder="Np. 50000"
              aria-label="Cel miesięczny przychodu"
            />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs font-semibold text-text-secondary">Cel roczny</span>
            <Input
              type="number"
              min="0"
              step="1000"
              value={annualDraft}
              onChange={(event) => setAnnualDraft(event.target.value)}
              placeholder="Np. 600000"
              aria-label="Cel roczny przychodu"
            />
          </label>
        </form>
      </Modal>
    </Card>
  );
}

function OperationalTile({ icon: Icon, label, value, to }: { icon: ElementType; label: string; value: string; to: string }) {
  return (
    <Link to={to} className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary/40 rounded-card">
      <Card variant="interactive" padding="sm" className="flex h-full min-h-[112px] items-center gap-4">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-surface-hover text-accent-primary">
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="text-xxs font-semibold uppercase text-text-muted">{label}</p>
          <p className="mt-1 text-xl font-bold text-text-primary tabular-nums">{value}</p>
        </div>
      </Card>
    </Link>
  );
}

function ChecklistTile({ activeScope, setActiveScope }: {
  activeScope: DashboardChecklistScope;
  setActiveScope: (scope: DashboardChecklistScope) => void;
}) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const { data, isLoading } = useQuery({
    queryKey: queryKeys.checklist(activeScope),
    queryFn: async () => (await dashboardApi.getChecklist(activeScope)).data.data,
  });

  const createMutation = useMutation({
    mutationFn: dashboardApi.createChecklistTask,
    onSuccess: () => {
      setTitle('');
      queryClient.invalidateQueries({ queryKey: queryKeys.checklist(activeScope) });
      queryClient.invalidateQueries({ queryKey: queryKeys.summary });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: dashboardApi.toggleChecklistTask,
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.checklist(activeScope) });
      const previous = queryClient.getQueryData<DashboardChecklistTask[]>(queryKeys.checklist(activeScope));
      queryClient.setQueryData<DashboardChecklistTask[]>(queryKeys.checklist(activeScope), old =>
        old?.map(task => task.id === id ? { ...task, completedAt: task.completedAt ? null : new Date().toISOString() } : task) ?? old
      );
      return { previous };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(queryKeys.checklist(activeScope), ctx.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.checklist(activeScope) });
      queryClient.invalidateQueries({ queryKey: queryKeys.summary });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: dashboardApi.deleteChecklistTask,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.checklist(activeScope) });
      queryClient.invalidateQueries({ queryKey: queryKeys.summary });
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!title.trim() || activeScope === 'DONE') return;
    createMutation.mutate({ title, scope: activeScope });
  };

  return (
    <Card padding="md" className="col-span-12 lg:col-span-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xxs font-semibold uppercase text-text-muted">Checklist</p>
          <h2 className="mt-1 text-xl font-bold text-text-primary">Operacje do zamknięcia</h2>
        </div>
        <Tabs value={activeScope} onValueChange={(value) => setActiveScope(value as DashboardChecklistScope)}>
          <TabsList>
            <TabsTrigger value="DAY">Dzień</TabsTrigger>
            <TabsTrigger value="WEEK">Tydz.</TabsTrigger>
            <TabsTrigger value="MONTH">Mies.</TabsTrigger>
            <TabsTrigger value="DONE">Wyk.</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      {activeScope !== 'DONE' && (
        <form onSubmit={submit} className="mt-5 grid grid-cols-[minmax(0,1fr)_auto] gap-2">
          <Input value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Dodaj zadanie i naciśnij Enter" />
          <Button type="submit" variant="icon" aria-label="Dodaj zadanie" loading={createMutation.isPending} leftIcon={<Plus />}>
            Dodaj
          </Button>
        </form>
      )}
      <div className="mt-5 space-y-2">
        {isLoading && Array.from({ length: 4 }).map((_, index) => <Skeleton key={index} className="h-11" />)}
        {!isLoading && data?.map(task => (
          <div key={task.id} className="flex items-center gap-3 rounded-xl border border-border-subtle bg-surface-hover/25 px-3 py-2.5">
            <button
              type="button"
              onClick={() => toggleMutation.mutate(task.id)}
              className={cn(
                'flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary/40',
                task.completedAt ? 'border-success bg-success text-white' : 'border-border bg-surface-base text-transparent',
              )}
              aria-label={task.completedAt ? 'Oznacz jako niewykonane' : 'Oznacz jako wykonane'}
            >
              <Check className="h-3.5 w-3.5" />
            </button>
            <p className={cn('min-w-0 flex-1 text-sm text-text-primary', task.completedAt && 'text-text-muted line-through')}>
              {task.title}
            </p>
            <button
              type="button"
              onClick={() => deleteMutation.mutate(task.id)}
              className="rounded-lg p-1.5 text-text-muted hover:bg-danger-bg hover:text-danger focus:outline-none focus-visible:ring-2 focus-visible:ring-danger/40"
              aria-label="Usuń zadanie"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
        {!isLoading && (!data || data.length === 0) && (
          <EmptyState
            icon={ClipboardCheck}
            title={activeScope === 'DONE' ? 'Brak wykonanych zadań' : 'Lista jest pusta'}
            description={activeScope === 'DONE' ? 'Wykonane zadania pojawią się tutaj.' : 'Dodaj jedno zadanie, które realnie przesunie wynik.'}
            size="sm"
          />
        )}
      </div>
    </Card>
  );
}

function QuoteTipStack({ summary }: { summary: DashboardSummary }) {
  return (
    <Card padding="md" className="col-span-12 flex h-full min-h-[360px] flex-col lg:col-span-6">
      <div className="grid flex-1 grid-rows-2 gap-4">
        <div className="flex min-h-0 flex-col justify-between rounded-xl border border-border-subtle bg-surface-hover/20 p-4">
          <Badge variant="accent" leftIcon={<Sparkles className="h-3.5 w-3.5" />}>Cytat dnia</Badge>
          <blockquote className="mt-4 text-xl font-semibold leading-tight text-text-primary">
            {summary.quote.text}
          </blockquote>
          <p className="mt-4 text-sm text-text-secondary">{summary.quote.author ?? 'EcomFlow'}</p>
        </div>
        <div className="flex min-h-0 flex-col justify-between rounded-xl border border-border-subtle bg-surface-hover/20 p-4">
          <div className="flex items-center justify-between gap-4">
            <Badge variant="info">{formatCategory(summary.tip.category)}</Badge>
            <span className="text-xs text-text-muted">Rada {summary.tip.dayOfYear}/366</span>
          </div>
          <p className="mt-4 text-xl font-semibold leading-tight text-text-primary">{summary.tip.text}</p>
          <p className="mt-4 text-sm text-text-secondary">Zamknij jedną konkretną akcję przed końcem dnia.</p>
        </div>
      </div>
    </Card>
  );
}

function HeatmapTile({ heatmap }: { heatmap?: DashboardHeatmap }) {
  const dayLabels = ['Pon', 'Wt', 'Śr', 'Czw', 'Pt', 'Sob', 'Nd'];
  const hours = Array.from({ length: 24 }, (_, hour) => hour);
  const [tooltip, setTooltip] = useState<{ text: string; x: number; y: number } | null>(null);
  const dayTotals = useMemo(
    () => dayLabels.map((_, dayIndex) => heatmap?.matrix[dayIndex]?.hours.reduce((sum, item) => sum + item.count, 0) ?? 0),
    [heatmap],
  );
  const showTooltip = (target: HTMLElement, text: string) => {
    const rect = target.getBoundingClientRect();
    setTooltip({
      text,
      x: Math.min(window.innerWidth - 16, Math.max(16, rect.left + rect.width / 2)),
      y: Math.max(16, rect.top - 8),
    });
  };

  return (
    <Card padding="md" className="col-span-12 overflow-hidden">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-xxs font-semibold uppercase text-text-muted">Heatmapa zamówień</p>
          <h2 className="mt-1 text-xl font-bold text-text-primary">Ostatnie {heatmap?.days ?? 30} dni</h2>
        </div>
        <BarChart3 className="h-5 w-5 text-accent-primary" />
      </div>
      <div className="mt-5 overflow-x-auto pb-2">
        <div className="min-w-[1280px] space-y-2">
          <div className="grid grid-cols-[56px_repeat(24,minmax(44px,1fr))] items-center gap-1">
            <span className="text-[10px] font-semibold uppercase text-text-muted">Godz.</span>
            {hours.map(hour => (
              <span key={hour} className="text-center text-[10px] tabular-nums text-text-muted">
                {String(hour).padStart(2, '0')}:00
              </span>
            ))}
          </div>
          {dayLabels.map((label, dayIndex) => (
            <div key={label} className="grid grid-cols-[56px_repeat(24,minmax(44px,1fr))] items-center gap-1">
              <span className="text-xs text-text-muted">{label}</span>
              {hours.map(hour => {
                const count = heatmap?.matrix[dayIndex]?.hours[hour]?.count ?? 0;
                const dayTotal = dayTotals[dayIndex] ?? 0;
                const dayShare = dayTotal > 0 ? Math.round((count / dayTotal) * 100) : 0;
                const intensity = heatmap?.max ? count / heatmap.max : 0;
                const tooltip = `${label} ${String(hour).padStart(2, '0')}:00 - ${count} zamówień (${dayShare}% / dzień)`;
                return (
                  <div
                    key={hour}
                    tabIndex={0}
                    aria-label={tooltip}
                    onMouseEnter={event => showTooltip(event.currentTarget, tooltip)}
                    onMouseLeave={() => setTooltip(null)}
                    onFocus={event => showTooltip(event.currentTarget, tooltip)}
                    onBlur={() => setTooltip(null)}
                    className="h-6 rounded-md border border-border-subtle focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary/50"
                    style={{ backgroundColor: `rgb(var(--accent-primary) / ${0.08 + intensity * 0.72})` }}
                  />
                );
              })}
            </div>
          ))}
        </div>
      </div>
      {tooltip && createPortal(
        <div
          role="tooltip"
          className="pointer-events-none fixed z-50 -translate-x-1/2 -translate-y-full whitespace-nowrap rounded-lg border border-border bg-surface-overlay px-2 py-1 text-xxs text-text-primary shadow-lg"
          style={{ left: tooltip.x, top: tooltip.y }}
        >
          {tooltip.text}
        </div>,
        document.body,
      )}
    </Card>
  );
}

function AchievementsWall({ achievements }: { achievements?: DashboardAchievement[] }) {
  const [expanded, setExpanded] = useState(false);
  const groups = useMemo(() => {
    const byCategory = new Map<string, DashboardAchievement[]>();
    for (const achievement of achievements ?? []) {
      const category = achievement.category || 'Inne';
      byCategory.set(category, [...(byCategory.get(category) ?? []), achievement]);
    }
    return [...byCategory.entries()].map(([category, items]) => ({
      category,
      unlocked: items.filter(item => item.unlockedAt).length,
      items,
      total: items.length,
      current: [...items].reverse().find(item => item.unlockedAt) ?? null,
      next: items.find(item => !item.unlockedAt) ?? null,
    })).filter(group => group.items.length > 0);
  }, [achievements]);

  const renderAchievementSummary = (
    achievement: DashboardAchievement | null,
    emptyTitle: string,
    emptyDescription: string,
  ) => (
    <div className={cn(
      'min-w-0 rounded-xl border p-4',
      achievement?.unlockedAt ? 'border-success-border bg-success-bg' : 'border-border-subtle bg-surface-hover/25',
    )}>
      {achievement ? (
        <div className="flex items-start gap-3">
          <div className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-xl', achievement.unlockedAt ? 'bg-success text-white' : 'bg-surface-muted text-text-muted')}>
            <Award className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h4 className="text-sm font-semibold text-text-primary">{achievement.label}</h4>
            <p className="mt-1 text-xs text-text-secondary">{achievement.description}</p>
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-surface-muted text-text-muted">
            <Award className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h4 className="text-sm font-semibold text-text-primary">{emptyTitle}</h4>
            <p className="mt-1 text-xs text-text-secondary">{emptyDescription}</p>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <Card padding="md" className="col-span-12">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-xxs font-semibold uppercase text-text-muted">Odznaki</p>
          <h2 className="mt-1 text-xl font-bold text-text-primary">Ściana osiągnięć</h2>
        </div>
        <Button variant="ghost" size="sm" rightIcon={<ChevronDown className={cn('h-4 w-4 transition-transform', expanded && 'rotate-180')} />} onClick={() => setExpanded(value => !value)}>
          {expanded ? 'Zwiń' : 'Pokaż wszystkie'}
        </Button>
      </div>
      <div className="mt-5 space-y-6">
        {!expanded && (
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            {groups.map(group => (
              <section key={group.category} className="rounded-xl border border-border-subtle bg-surface-hover/15 p-4">
                <div className="mb-3 flex items-center justify-between gap-4">
                  <h3 className="text-sm font-semibold text-text-primary">{group.category}</h3>
                  <span className="text-xs tabular-nums text-text-muted">{group.unlocked}/{group.total}</span>
                </div>
                <div className="grid grid-cols-[minmax(0,1fr)_32px_minmax(0,1fr)] items-stretch gap-3">
                  {renderAchievementSummary(
                    group.current,
                    'Żadne wyzwanie nie zostało osiągnięte',
                    group.next
                      ? `Zrób "${group.next.label}", by odblokować pierwsze osiągnięcie.`
                      : 'Ta sekcja czeka na pierwsze wyzwanie.',
                  )}
                  <div className="flex items-center justify-center text-text-muted">
                    <ArrowRight className="h-5 w-5" />
                  </div>
                  {renderAchievementSummary(
                    group.next,
                    'Wszystko zdobyte',
                    'Ta sekcja ma komplet odblokowanych osiągnięć.',
                  )}
                </div>
              </section>
            ))}
          </div>
        )}
        {expanded && groups.map(group => (
          <section key={group.category}>
            <div className="mb-3 flex items-center justify-between gap-4">
              <h3 className="text-sm font-semibold text-text-primary">{group.category}</h3>
              <span className="text-xs tabular-nums text-text-muted">{group.unlocked}/{group.total}</span>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
              {group.items.map(achievement => (
                <div
                  key={achievement.code}
                  className={cn(
                    'rounded-xl border p-4',
                    achievement.unlockedAt
                      ? 'border-success-border bg-success-bg'
                      : 'border-border-subtle bg-surface-hover/25 opacity-70',
                  )}
                >
                  <div className="flex items-start gap-3">
                    <div className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-xl', achievement.unlockedAt ? 'bg-success text-white' : 'bg-surface-muted text-text-muted')}>
                      <Award className="h-5 w-5" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-text-primary">{achievement.label}</h4>
                      <p className="mt-1 text-xs text-text-secondary">{achievement.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}
        {!achievements && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, index) => <Skeleton key={index} className="h-24" />)}
          </div>
        )}
        {achievements && achievements.length === 0 && (
          <EmptyState
            icon={Award}
            title="Brak odznak"
            description="Odznaki pojawią się po przeliczeniu dashboardu."
            size="sm"
          />
        )}
      </div>
    </Card>
  );
}

function DashboardSkeleton() {
  return (
    <div className="grid grid-cols-12 gap-4">
      <Skeleton className="col-span-12 h-64 lg:col-span-6" />
      <Skeleton className="col-span-12 h-64 lg:col-span-6" />
      {Array.from({ length: 6 }).map((_, index) => <Skeleton key={index} className="col-span-12 h-40 md:col-span-6 lg:col-span-2" />)}
      <Skeleton className="col-span-12 h-80 lg:col-span-6" />
      <Skeleton className="col-span-12 h-80 lg:col-span-6" />
    </div>
  );
}

export function DashboardV2Page() {
  const queryClient = useQueryClient();
  const [activeScope, setActiveScope] = useState<DashboardChecklistScope>('DAY');

  const summaryQuery = useQuery({
    queryKey: queryKeys.summary,
    queryFn: async () => (await dashboardApi.getSummary()).data,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
  const summaryReady = summaryQuery.isSuccess;
  const heatmapQuery = useQuery({
    queryKey: queryKeys.heatmap,
    queryFn: async () => (await dashboardApi.getHeatmap()).data,
    staleTime: 60_000,
    enabled: summaryReady,
    refetchOnWindowFocus: false,
  });
  const achievementsQuery = useQuery({
    queryKey: queryKeys.achievements,
    queryFn: async () => (await dashboardApi.getAchievements()).data.data,
    staleTime: 60_000,
    enabled: summaryReady,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    const cleanup = onSocketReady((socket) => {
      const handleOrderCreated = () => {
        queryClient.invalidateQueries({ queryKey: queryKeys.summary });
        queryClient.invalidateQueries({ queryKey: queryKeys.heatmap });
      };
      const invalidateGamification = () => {
        queryClient.invalidateQueries({ queryKey: queryKeys.summary });
        queryClient.invalidateQueries({ queryKey: queryKeys.achievements });
      };
      socket.off('dashboard:order-created', handleOrderCreated);
      socket.off('dashboard:level-up', invalidateGamification);
      socket.off('dashboard:achievement', invalidateGamification);
      socket.on('dashboard:order-created', handleOrderCreated);
      socket.on('dashboard:level-up', invalidateGamification);
      socket.on('dashboard:achievement', invalidateGamification);
    });

    return () => {
      cleanup?.();
      const socket = getSocket();
      socket?.off('dashboard:order-created');
      socket?.off('dashboard:level-up');
      socket?.off('dashboard:achievement');
    };
  }, [queryClient]);

  const summary = summaryQuery.data;
  const metricClassName = 'col-span-12 sm:col-span-6 xl:col-span-2';

  if (summaryQuery.isLoading || !summary) {
    return (
      <div className={DASHBOARD_PAGE_CLASS}>
        <DashboardSkeleton />
      </div>
    );
  }

  return (
    <div className={DASHBOARD_PAGE_CLASS}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xxs font-semibold uppercase text-text-muted">Dashboard</p>
          <h1 className="text-3xl font-bold text-text-primary">Centrum operacyjne</h1>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4">
        <CharacterHero summary={summary} />
        <GoalTile summary={summary} />

        <MetricTile className={metricClassName} label="Przychód" period="Ten miesiąc" value={formatPLN(summary.finance.revenueMTD)} delta={summary.finance.deltas.revenueMTD} sparkline={summary.finance.sparklines.revenue} icon={CircleDollarSign} to="/finance" tone="success" />
        <MetricTile className={metricClassName} label="Dochód" period="Ten miesiąc" value={formatPLN(summary.finance.incomeMTD)} delta={summary.finance.deltas.incomeMTD} sparkline={summary.finance.sparklines.income} icon={BarChart3} to="/finance/cashflow" tone={summary.finance.incomeMTD >= 0 ? 'success' : 'danger'} />
        <MetricTile className={metricClassName} label="Zysk" period="Ten miesiąc" value={formatPLN(summary.finance.profitMTD)} delta={summary.finance.deltas.profitMTD} sparkline={summary.finance.sparklines.profit} icon={LineChart} to="/finance" tone={summary.finance.profitMTD >= 0 ? 'success' : 'danger'} />
        <MetricTile className={metricClassName} label="Zamówienia" period="Ten miesiąc" value={formatNumber(summary.logistics.ordersMTD)} delta={summary.logistics.deltas.ordersMTD} sparkline={summary.logistics.sparklines.orders} icon={ShoppingCart} to="/orders" />
        <MetricTile className={metricClassName} label="Sprzedane szt." period="Ten miesiąc" value={formatNumber(summary.logistics.itemsSoldMTD)} delta={summary.logistics.deltas.itemsSoldMTD} sparkline={summary.logistics.sparklines.items} icon={PackageCheck} to="/orders" />
        <MetricTile className={metricClassName} label="AOV" period="Ten miesiąc" value={formatPLN(summary.logistics.aovMTD)} delta={summary.logistics.deltas.aovMTD} sparkline={summary.finance.sparklines.revenue} icon={CircleDollarSign} to="/finance" />

        <div className="col-span-12 grid grid-cols-1 gap-4 md:grid-cols-3">
          <OperationalTile icon={Truck} label="Logistyka" value={`${formatNumber(summary.logistics.toPackCount)} do spakowania`} to="/orders" />
          <OperationalTile icon={Inbox} label="Inbox" value={`${summary.inbox.unreadCount} nieprzecz.`} to="/messages" />
          <OperationalTile icon={TestTube2} label="Laboratorium" value={`${summary.lab.toTestCount} oczekuje`} to="/marketing/laboratorium" />
        </div>

        <ChecklistTile activeScope={activeScope} setActiveScope={setActiveScope} />
        <QuoteTipStack summary={summary} />
        <HeatmapTile heatmap={heatmapQuery.data} />
        <AchievementsWall achievements={achievementsQuery.data} />
      </div>
    </div>
  );
}
